// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AttachmentTableRow } from '@/components/attachment-table-row';
import { UploadFileOutlined } from '@/constants/hardfin-standard-icons';
import { Attachment, File } from '@/file/api/types';
import { URLLink } from '@/url-link/api/types';
import {
  Card,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

interface Props {
  /**
   * A collection of files to display.
   */
  files: File[];

  /**
   * A collection of URL links to display.
   */
  urlLinks: URLLink[];

  /**
   * Pre-filtered attachments to display based on selected tab.
   */
  filteredAttachments: Attachment[];

  /**
   * A callback fired when a file delete button is pressed.
   */
  onDeleteFile: (file: File) => void;

  /**
   * A callback fired when a URL link delete button is pressed.
   */
  onDeleteURLLink: (urlLink: URLLink) => void;

  /**
   * A callback fired when a URL link edit button is pressed.
   */
  onEditURLLink?: (urlLink: URLLink) => void;
}

/**
 * A table component that displays files and URL links together.
 * Tabs are now handled by the parent component following the tickets page pattern.
 */
export function AttachmentsTable(props: Props) {
  const handleDelete = (attachment: Attachment) => {
    if (attachment.type === 'file') {
      // Find the original file object to pass to the callback
      const originalFile = props.files.find((f) => f.id === attachment.id);
      if (originalFile) {
        props.onDeleteFile(originalFile);
      }
    } else {
      // Find the original URL link object to pass to the callback
      const originalURLLink = props.urlLinks.find(
        (link) => link.id === attachment.id
      );
      if (originalURLLink) {
        props.onDeleteURLLink(originalURLLink);
      }
    }
  };

  const handleEdit = (attachment: Attachment) => {
    if (attachment.type === 'link' && props.onEditURLLink) {
      // Find the original URL link object to pass to the callback
      const originalURLLink = props.urlLinks.find(
        (link) => link.id === attachment.id
      );
      if (originalURLLink) {
        props.onEditURLLink(originalURLLink);
      }
    }
  };

  if (props.filteredAttachments.length === 0) {
    return (
      <Card
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 4,
          textAlign: 'center',
          minHeight: '200px',
        }}
      >
        <UploadFileOutlined
          sx={{ fontSize: 48, color: 'text.secondary', marginBottom: 2 }}
        />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No files or links yet
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: '400px', marginBottom: 3 }}
        >
          Attach files and links related to this project
        </Typography>
      </Card>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '48px', padding: '8px 16px' }} />
            <TableCell>Name</TableCell>
            <TableCell>Updated date</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.filteredAttachments.map((attachment) => (
            <AttachmentTableRow
              key={`${attachment.type}-${attachment.id}`}
              attachment={attachment}
              onDelete={() => handleDelete(attachment)}
              onEdit={
                attachment.type === 'link'
                  ? () => handleEdit(attachment)
                  : undefined
              }
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
